import React from "react";
import { X } from "lucide-react";
import {
  CASH_RECEIPT_USAGE_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  NTS_STATE_LABELS,
  type InvoiceItem,
  type TaxDocumentBuyer,
} from "../../../../domain/types/tax-document";
import { resolveErrorMessage } from "../../../../shared/utils/save-flow";
import { InvoiceAdjustmentSection } from "./InvoiceAdjustmentSection";
import { TaxDocumentBuyerForm, toBuyerPayload } from "./TaxDocumentBuyerForm";
import { openUrlFromServer, taxDocumentApi } from "./tax-document-api";

interface Props {
  companyId: number;
  invoice: InvoiceItem;
  /** 홈택스 발급 연동 여부. 미연동이면 수기 처리만 할 수 있다 */
  connected: boolean;
  onClose: () => void;
  onChanged: (next: InvoiceItem) => void;
}

const formatDateTime = (value: string | null) =>
  value
    ? new Date(value).toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

/**
 * 세금증빙 상세 + 구매자 정보 수정 + 발급/취소/새로고침/보기/수기 처리.
 *
 * - 발급 전(미발행·실패·취소): 구매자 정보를 고치고 발급한다
 * - 발급 후: 국세청 전송 결과를 보고, 증빙을 열어보거나 취소한다
 */
export const InvoiceDetailDialog: React.FC<Props> = ({ companyId, invoice, connected, onClose, onChanged }) => {
  const [buyer, setBuyer] = React.useState<TaxDocumentBuyer>(invoice);
  const [memo, setMemo] = React.useState(invoice.memo ?? "");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  React.useEffect(() => {
    setBuyer(invoice);
    setMemo(invoice.memo ?? "");
  }, [invoice]);

  const editable = invoice.status !== "SUCCESS";
  const issuable =
    connected &&
    invoice.orderPaid &&
    (invoice.status !== "SUCCESS" || invoice.ntsState === "SEND_FAILED");

  const run = async (action: () => Promise<InvoiceItem | void>, done?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const next = await action();
      if (next) onChanged(next);
      if (done) setNotice(done);
    } catch (e) {
      setError(resolveErrorMessage(e, "처리에 실패했습니다."));
    } finally {
      setBusy(false);
    }
  };

  const saveBuyer = () =>
    taxDocumentApi.updateBuyer(companyId, invoice.id, { ...toBuyerPayload(buyer), memo: memo.trim() || null });

  const handleSave = () => run(saveBuyer, "저장했습니다.");

  const handleIssue = () => {
    const label = INVOICE_TYPE_LABELS[buyer.invoiceType];
    if (!window.confirm(`${label}를 국세청으로 발급하시겠습니까?\n발급 후에는 내용을 고칠 수 없습니다.`)) return;
    run(async () => {
      // 화면에서 고친 내용을 먼저 저장하고 발급한다
      if (editable) await saveBuyer();
      return taxDocumentApi.issue(companyId, invoice.id);
    }, `${label}를 발급했습니다.`);
  };

  const handleCancel = () => {
    const reason = window.prompt(
      invoice.invoiceType === "CASH_RECEIPT"
        ? "취소 사유를 입력해 주세요.\n국세청 전송 후에는 취소 현금영수증이 새로 발급됩니다."
        : "취소 사유를 입력해 주세요.\n세금계산서는 국세청 전송 전에만 취소할 수 있습니다.",
      "주문 취소",
    );
    if (reason === null) return;
    run(() => taxDocumentApi.cancel(companyId, invoice.id, reason), "취소했습니다.");
  };

  const handleRefresh = () => run(() => taxDocumentApi.refresh(companyId, invoice.id), "국세청 결과를 가져왔습니다.");

  const handleView = () =>
    run(() => openUrlFromServer(() => taxDocumentApi.viewUrl(companyId, invoice.id)));

  const handleManual = () => {
    const next = invoice.status === "SUCCESS" ? "PENDING" : "SUCCESS";
    const msg =
      next === "SUCCESS"
        ? "홈택스 등에서 직접 발급한 건으로 표시합니다. 이 화면에서 국세청으로 발급하지 않습니다. 계속할까요?"
        : "미발행으로 되돌리시겠습니까?";
    if (!window.confirm(msg)) return;
    run(() => taxDocumentApi.updateStatus(companyId, invoice.id, next, memo.trim() || null));
  };

  // 조정 건을 처리하면 원본 증빙의 처리 대기 건수·상태가 바뀌므로 다시 받아 목록에도 반영한다
  const handleAdjustmentProcessed = async () => {
    try {
      onChanged(await taxDocumentApi.get(companyId, invoice.id));
    } catch (e) {
      console.error("증빙 다시 불러오기 실패:", e);
    }
  };

  const handleMemoOnly = () =>
    run(() => taxDocumentApi.updateStatus(companyId, invoice.id, invoice.status, memo.trim() || null), "메모를 저장했습니다.");

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40">
      <div className="w-[640px] max-h-[90vh] rounded-[12px] bg-white shadow-lg overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#eee]">
          <div className="flex items-center gap-2">
            <h3 className="text-[16px] font-bold text-[#222]">세금증빙 상세</h3>
            <Badge tone={invoice.status === "SUCCESS" ? "blue" : "gray"}>{INVOICE_STATUS_LABELS[invoice.status]}</Badge>
            {invoice.ntsState && <Badge tone="gray">국세청 {NTS_STATE_LABELS[invoice.ntsState]}</Badge>}
          </div>
          <button onClick={onClose} className="text-[#999] hover:text-[#666]" aria-label="닫기">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex flex-col gap-5">
          {error && (
            <div className="rounded-[8px] border border-[#F24A4A]/20 bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#F24A4A]">{error}</div>
          )}
          {notice && (
            <div className="rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4]">{notice}</div>
          )}
          {invoice.orderCanceledAfterIssue && (
            <div className="rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4]">
              주문이 취소됐습니다. 입금액을 돌려줬다면 주문을 '환불' 처리하세요. 증빙이 자동으로 취소되거나 수정세금계산서
              초안이 만들어집니다. 돌려주지 않았다면 이 증빙은 그대로 두면 됩니다.
            </div>
          )}
          {invoice.errorMessage && !error && (
            <div className="rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4]">
              최근 발급 실패 사유: {invoice.errorMessage}
            </div>
          )}

          <section className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
            <Row label="주문번호" value={invoice.orderNumber ?? "-"} />
            <Row label="주문자" value={invoice.purchaserName ?? "-"} />
            <Row label="주문명" value={invoice.orderName} />
            <Row label="결제" value={`${invoice.paymentMethod === "BANK_TRANSFER" ? "무통장입금" : invoice.paymentMethod ?? "-"} · ${invoice.orderPaid ? "결제확인" : "결제 전"}`} />
            <Row label="합계금액" value={`${invoice.orderTotalAmount.toLocaleString()}원`} />
            <Row
              label="공급가액 / 부가세"
              value={`${(invoice.orderTotalAmount - Math.floor(invoice.orderTotalAmount / 11)).toLocaleString()}원 / ${Math.floor(invoice.orderTotalAmount / 11).toLocaleString()}원`}
            />
            {invoice.status !== "PENDING" && (
              <>
                <Row label="발행일시" value={formatDateTime(invoice.issuedAt)} />
                <Row label="국세청 승인번호" value={invoice.approvalNo ?? "-"} />
                {invoice.canceledAt && <Row label="취소일시" value={formatDateTime(invoice.canceledAt)} />}
                <Row label="발급 방식" value={invoice.issuedViaIntegration ? "연동 발급" : "수기 처리"} />
              </>
            )}
            {invoice.invoiceType === "CASH_RECEIPT" && invoice.cashReceiptUsage && !editable && (
              <Row label="거래용도" value={CASH_RECEIPT_USAGE_LABELS[invoice.cashReceiptUsage]} />
            )}
          </section>

          <InvoiceAdjustmentSection
            companyId={companyId}
            invoiceId={invoice.id}
            onProcessed={handleAdjustmentProcessed}
          />

          <section>
            <h4 className="text-[14px] font-bold text-[#222] mb-3">받는 분 정보</h4>
            <TaxDocumentBuyerForm value={buyer} onChange={setBuyer} disabled={!editable || busy} />
          </section>

          <section>
            <h4 className="text-[14px] font-bold text-[#222] mb-2">메모</h4>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 text-[13px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
            />
          </section>

          {!connected && (
            <p className="text-[12px] text-[#888]">
              홈택스 발급 연동 전이라 이 화면에서 발급할 수 없습니다. 내 회사 정보 &gt; 추가 서비스 연동에서 홈택스를 연동하거나, 직접 발급한 뒤 수기 처리해 주세요.
            </p>
          )}
          {connected && !invoice.orderPaid && invoice.status !== "SUCCESS" && (
            <p className="text-[12px] text-[#888]">결제가 확인되면 발급할 수 있습니다.</p>
          )}
        </div>

        <div className="p-4 border-t border-[#eee] flex flex-wrap items-center gap-2">
          {editable && (
            <button disabled={busy} onClick={handleSave} className={secondaryBtn}>
              저장
            </button>
          )}
          {!editable && (
            <button disabled={busy} onClick={handleMemoOnly} className={secondaryBtn}>
              메모 저장
            </button>
          )}
          {!invoice.issuedViaIntegration && invoice.status !== "CANCELED" && (
            <button disabled={busy} onClick={handleManual} className={textBtn}>
              {invoice.status === "SUCCESS" ? "미발행으로 되돌리기" : "수기 발행 처리"}
            </button>
          )}
          <div className="flex-1" />
          {invoice.issuedViaIntegration && (
            <>
              <button disabled={busy} onClick={handleRefresh} className={secondaryBtn}>
                국세청 결과 새로고침
              </button>
              <button disabled={busy} onClick={handleView} className={secondaryBtn}>
                증빙 보기
              </button>
            </>
          )}
          {invoice.status === "SUCCESS" && (
            <button disabled={busy} onClick={handleCancel} className={dangerBtn}>
              발급 취소
            </button>
          )}
          {issuable && (
            <button disabled={busy} onClick={handleIssue} className={primaryBtn}>
              {busy ? "처리 중..." : invoice.status === "SUCCESS" ? "다시 발급" : "발급하기"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const primaryBtn = "px-4 h-[36px] text-[13px] rounded-[6px] bg-[#222] text-white hover:bg-[#333] disabled:bg-[#ccc]";
const secondaryBtn =
  "px-3 h-[36px] text-[13px] rounded-[6px] bg-white text-[#222] border border-[#222] hover:bg-[#f5f5f5] disabled:opacity-50";
const textBtn = "px-2 h-[36px] text-[13px] text-[#4285F4] hover:text-[#3367D6] disabled:opacity-50";
const dangerBtn = "px-3 h-[36px] text-[13px] text-[#F24A4A] hover:text-[#D63939] disabled:opacity-50";

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-start py-1.5 border-b border-[#f5f5f5]">
    <span className="w-[110px] text-[#888] shrink-0">{label}</span>
    <span className="text-[#222] break-all">{value}</span>
  </div>
);

export const Badge: React.FC<{ tone: "blue" | "gray"; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded-[4px] text-[11px] font-medium ${
      tone === "blue" ? "bg-[#E8EFFF] text-[#4285F4]" : "bg-[#f5f5f5] text-[#888]"
    }`}
  >
    {children}
  </span>
);
